import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { AlertTriangle, Phone, MapPin, Heart, Siren, ShieldAlert, Building2, Ambulance } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const Emergency = ({ user }) => {
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(null);
  const [isPinging, setIsPinging] = useState(false);

  const emergencyNumbers = [
    { label: "Emergency Helpline", number: "112", icon: Siren },
    { label: "Ambulance", number: "102", icon: Ambulance },
    { label: "Police", number: "100", icon: ShieldAlert },
    { label: "Fire", number: "101", icon: AlertTriangle },
  ];

  const triggerEmergencyPing = () => {
    setCountdown(5);
    setIsPinging(true);
    
    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          // Trigger emergency actions
          toast.success("Emergency ping sent!", {
            description: "Your location has been shared with emergency contacts.",
          });
          setIsPinging(false);
          return null;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const cancelPing = () => {
    setCountdown(null);
    setIsPinging(false);
    toast.info("Emergency ping cancelled");
  };

  const callNumber = (number) => {
    window.open(`tel:${number}`, "_self");
  };

  return (
    <div className="min-h-screen pb-24 bg-gradient-to-br from-red-50 via-white to-red-50 emergency-mode">
      {/* Header */}
      <div className="bg-red-500 px-4 py-4">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
            <AlertTriangle className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="font-heading font-semibold text-white">Emergency</h1>
            <p className="text-xs text-red-100">Get immediate help</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 py-6">
        {/* Emergency Ping Button */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center mb-8"
        >
          <p className="text-slate-600 mb-6">
            {isPinging
              ? "Sending emergency ping..."
              : "Press and hold to send emergency ping to your contacts"}
          </p>

          {isPinging ? (
            <motion.div
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 0.5, repeat: Infinity }}
              className="relative inline-block"
            >
              <div className="w-40 h-40 bg-red-500 rounded-full flex items-center justify-center shadow-2xl emergency-pulse mx-auto">
                <div className="text-center text-white">
                  <span className="text-5xl font-bold">{countdown}</span>
                  <p className="text-sm">seconds</p>
                </div>
              </div>
              <Button
                variant="outline"
                className="mt-6 rounded-full border-red-300 text-red-600 hover:bg-red-50"
                onClick={cancelPing}
                data-testid="cancel-ping-btn"
              >
                Cancel
              </Button>
            </motion.div>
          ) : (
            <button
              onClick={triggerEmergencyPing}
              className="w-40 h-40 bg-gradient-to-br from-red-500 to-red-600 rounded-full flex items-center justify-center shadow-2xl sos-button mx-auto hover:from-red-600 hover:to-red-700 transition-all"
              data-testid="emergency-ping-btn"
            >
              <div className="text-center text-white">
                <Siren className="w-12 h-12 mx-auto mb-2" />
                <span className="text-lg font-bold">SOS</span>
              </div>
            </button>
          )}
        </motion.div>

        {/* Emergency Numbers */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <h2 className="font-heading text-lg font-semibold text-slate-800 mb-4">
            Emergency Numbers
          </h2>
          <div className="grid grid-cols-2 gap-3">
            {emergencyNumbers.map((item, index) => (
              <motion.div
                key={item.number}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 + index * 0.1 }}
                className="glass-card rounded-2xl p-4 cursor-pointer hover:bg-white/80 transition-all"
                onClick={() => callNumber(item.number)}
                data-testid={`emergency-${item.number}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
                    <item.icon className="w-5 h-5 text-red-500" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800">{item.number}</p>
                    <p className="text-xs text-slate-500">{item.label}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-8"
        >
          <h2 className="font-heading text-lg font-semibold text-slate-800 mb-4">
            Quick Actions
          </h2>
          <div className="space-y-3">
            <Button
              variant="outline"
              className="w-full rounded-xl justify-start h-14 border-slate-200"
              onClick={() => navigate("/nearby")}
              data-testid="find-hospital-btn"
            >
              <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center mr-3">
                <Building2 className="w-5 h-5 text-red-500" />
              </div>
              <div className="text-left">
                <p className="font-medium text-slate-800">Find Nearest Hospital</p>
                <p className="text-xs text-slate-500">Get directions immediately</p>
              </div>
            </Button>

            <Button
              variant="outline"
              className="w-full rounded-xl justify-start h-14 border-slate-200"
              onClick={() => navigate("/chat")}
              data-testid="talk-to-ai-btn"
            >
              <div className="w-10 h-10 bg-primary-100 rounded-xl flex items-center justify-center mr-3">
                <Heart className="w-5 h-5 text-primary-500" />
              </div>
              <div className="text-left">
                <p className="font-medium text-slate-800">Talk to AI Doctor</p>
                <p className="text-xs text-slate-500">Get immediate guidance</p>
              </div>
            </Button>
          </div>
        </motion.div>

        {/* Disclaimer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
          className="mt-8 p-4 bg-amber-50 rounded-xl border border-amber-200"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800">
              <p className="font-medium mb-1">Important Notice</p>
              <p className="text-amber-700">
                If you're experiencing a medical emergency, please call emergency services immediately.
                SupaDoc AI is for guidance only and cannot replace professional medical care.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Emergency;
